/* enum_radio.control_adapter.js */

const _ = require("lodash");
const underscoreString = require("underscore.string");
_.mixin(underscoreString.exports());
const EventEmitter = require("events").EventEmitter;
const q = require("q");
const InputRadioControlComponent = require("@gluenetworks/input_radio_control");
const StyleUtil = require("../StyleUtil");

/**
 * @event EnumControlAdapter#ValueChanged
 * @type {Object}
 * @property oldValue {string} The previous value
 * @property newValue {string} The new value
 */

/**
 * @class EnumRadioAdapter
 * @classdesc renders the Enum Radio button Jtype.
 * @implements AngularControlAdapter
 *
 * @param logger {Bunyan} The logger
 * @param containingElem {DomElement} The containing dom element for this control
 * @param formCtrlDef {FormControlDefinition} The definition of this control object.
 *
 * @requires util
 * @requires EventEmitter
 *
 * @fires EnumControlAdapter#ValueChanged
 */
class EnumRadioAdapter extends EventEmitter {
    /*
     * Tell the Form renderer that this is an Angular Control.
     * @returns {boolean}
     */
    static get isAngularControl() {
        return true;
    }

    /**
     * Tell the Form renderer that this adapter supports multi edit mode.
     * @returns {boolean}
     */
    static get isMultiEditSupported() {
        return true;
    }

    constructor(logger, containingElem, formCtrlDef) {
        super();
        this.formCtrlDef = formCtrlDef;
        this.containingElem = containingElem;
        this.logger = logger;
        this.name = this.formCtrlDef.name;
        this.loadDeferred = q.defer();
        this._multiEditMode = _.get(formCtrlDef, "displayModeOptions.multiEditMode", false);
        this._required = false;
        this._oldInvalidStatus = false;
        this.$init.$inject = ["$scope"];
        this.$compiledAndLinked.$inject = ["$scope", "$timeout"];
    }

    /********************************************************************************/
    /*** START Type Definitions                                                   ***/
    /********************************************************************************/

    /********************************************************************************/
    /*** END Type Definitions                                                     ***/
    /*** START Private API                                                        ***/
    /********************************************************************************/

    _onValueChanged() {
        this._fireValueChanged();
        this._updateValidityStatus();
    }
    /**
     * Fire the Value Changed Event
     * @private
     */
    _fireValueChanged() {
        //Get the new Value
        var newVal = this.getValue();
        this.emit("ValueChanged", {
            name: this.formCtrlDef.name,
            containingElem: this.containingElem,
            controlAdapter: this,
            formControlDefinition: this.formCtrlDef,
            oldValue: this._oldValue,
            newValue: newVal,
        });
        this._oldValue = newVal;
    }

    _fireInvalidStatusChanged() {
        let newInvalidStatus = this.isInvalid();
        if (newInvalidStatus !== this._oldInvalidStatus) {
            this.emit("Invalid", {
                name: this.formCtrlDef.name,
                containingElem: this.containingElem,
                controlAdapter: this,
                formControlDefinition: this.formCtrlDef,
                oldValue: this._oldInvalidStatus,
                newValue: newInvalidStatus,
            });
            this._oldInvalidStatus = newInvalidStatus;
        }
    }

    _updateValidityStatus() {
        let validationMessages = this.getValidationMessages();
        let errorsDiv = this.containingElem.find(".errors")[0];
        if (!_.isEmpty(validationMessages)) {
            this.containingElem[0].classList.add("invalid");
            this.enumComponent.setInvalid(true);
            errorsDiv.textContent = validationMessages[0];
        } else {
            this._validationMessages = [];
            this.containingElem[0].classList.remove("invalid");
            this.enumComponent.setInvalid(false);
            errorsDiv.textContent = "";
        }
        this._fireInvalidStatusChanged();
    }
    /********************************************************************************/
    /*** END Private API                                                          ***/
    /*** START Public API                                                         ***/
    /********************************************************************************/

    /**
     * Returns true if this control is failing any validations
     * @returns {boolean}
     */
    isInvalid() {
        if (this._multiEditMode && !this.getMultiEditActive()) {
            return false;
        }
        let value = this.getValue();
        return (
            this._required && !this.enumComponent.isDisabled() && (_.isNil(value) || value === "")
        );
    }

    /**
     * Returns list of validation messages
     * @returns {Array}
     */
    getValidationMessages() {
        return this.isInvalid() ? ["Please select an option"] : [];
    }

    /**
     * @returns the data associated with this control.
     */
    getValue() {
        let val = this.enumComponent.getValue();
        if (_.isUndefined(val)) {
            val = null;
        }

        return val;
    }

    /**
     * Set this controls current value.
     * @param value {string} The value to set this control to.
     * @returns the value associated with this control, it might not always be the same as the value passed in for
     * example adapters with a ValueScope.PARENT.
     */
    setValue(value) {
        this.enumComponent.setValue(value);
        return this.getValue();
    }

    getMultiEditActive() {
        return this._multiEditActive;
    }

    setMultiEditActive(flag) {
        if (!this._readOnlyField) {
            this.setReadOnly(!flag);
            let oldValue = this._multiEditActive;
            this._multiEditActive = flag;
            this._updateValidityStatus();
            if (oldValue !== this._multiEditActive) {
                this.emit("MultiEditActive", {
                    oldValue: oldValue,
                    newValue: this._multiEditActive,
                });
            }
        }
    }

    /**
     * Tear down this control
     */
    tearDown() {
        this.enumComponent.shutdown();
    }

    /********************************************************************************/
    /*** END Public API                                                           ***/
    /*** Start Angular/Render Work                                                ***/
    /********************************************************************************/

    /**
     * A Hook to start the angular work, due to timing issues don't try anything angularis before this hook is called.
     * @param $scope {$scope} Angular $scope
     * @parma $templateCache {$templateCache}
     */
    $init($scope) {
        this._scope = $scope;

        var opts = [];
        var useDisplayOpts = false;

        let styles = StyleUtil.getCssStyleString(
            _.get(this.formCtrlDef, "configSettings.Display.styles.cssStyle", {})
        );
        this.containingElem[0].style.cssText += styles;
        let cssClassList = _.get(this.formCtrlDef, "configSettings.Display.styles.cssClass", []);
        StyleUtil.appendClassListToElement(cssClassList, this.containingElem[0]);

        if (_.has(this.formCtrlDef, "configSettings.Display.enum")) {
            useDisplayOpts = true;
        }
        this._required = _.get(this.formCtrlDef, "validationInfo.required", false);
        this._required = this._required ? true : false;

        this.formCtrlDef.configSettings.Enum.forEach((option, idx) => {
            var label;
            if (useDisplayOpts) {
                label = this.formCtrlDef.configSettings.Display.enum[idx] || option;
                // Make sure it's a string
                label = "" + label;
            } else {
                label = option;
                if (!_.isString(option)) {
                    label = "" + option;
                }
            }
            opts.push({label: label, value: option});
        });

        this.enumComponent = new InputRadioControlComponent(opts);
        if (_.has(this.formCtrlDef, "configSettings.Display.radioButton.horizontal")) {
            this.enumComponent.setHorizontal(true);
        }
        this.enumComponent.setOptions(opts);
        this.enumComponent.on("Value", (this._onValueChanged = this._onValueChanged.bind(this)));

        let initialValue = this.formCtrlDef.value;

        if (this._multiEditMode) {
            initialValue = null;
        }

        //Setup directive
        this.enumComponent.renderer.render();
        let _errorsDiv = document.createElement("div");
        _errorsDiv.classList.add("errors");
        this.containingElem[0].appendChild(this.enumComponent.renderer.rootElement);
        this.containingElem[0].appendChild(_errorsDiv);

        this.enumComponent.setValue(initialValue);

        //Notify the world this adapter is ready to be compiled/linked and values retrieved
        this.emit("InitializationComplete", {
            initializedValue: this.getValue(),
        });
        return this.loadDeferred.promise;
    }

    /**
     * A Hook for when this control has been compiled/Linked.
     * @param $scope {$scope} The Angular Scope variable
     *
     * if $compiledAndLinked.$inject is specified those injection parameters will be used as the arguments
     */
    $compiledAndLinked($scope, $timeout) {
        // If the default value is a number then we have to update the
        // default value here because the initial value did not take.
        if (_.isNumber(this.formCtrlDef.value)) {
            this.enumComponent.setValue(this.formCtrlDef.value);
        }
        if (_.get(this.formCtrlDef, "configSettings.Display.readOnly")) {
            this._readOnlyField = true;
            this.setReadOnly(true);
        }

        // If this is multiEditMode, set the field as read only by default
        if (this._multiEditMode) {
            this.setReadOnly(true);
        }

        if (typeof this.formCtrlDef.configData !== "undefined") {
            //Update used/available based on configData
            this.setValue(this.formCtrlDef.configData);
            $timeout(() => {
                this.loadDeferred.resolve();
            });
        } else {
            this.loadDeferred.resolve();
        }
        this._updateValidityStatus();
    }

    /********************************************************************************/
    /*** END Angular/Render Work                                                  ***/
    /********************************************************************************/
    setReadOnly(flag) {
        if (flag) {
            this.enumComponent.setDisabled(true);
        } else {
            this.enumComponent.setDisabled(false);
        }
    }
}

module.exports = EnumRadioAdapter;
